/* src/components/game/GameControls.js */
import React, { useEffect, useState, useCallback } from 'react'; // Added useCallback
import MatchButton from './MatchButton';
import { useSettings } from '../../contexts/SettingsContext';

// Helper function to format key names for display
const formatKeyForDisplay = (keyName) => {
  if (!keyName) return '?';
  if (keyName === ' ') return 'Space';
  if (keyName === 'ArrowLeft') return '←';
  if (keyName === 'ArrowRight') return '→';
  if (keyName === 'ArrowUp') return '↑';
  if (keyName === 'ArrowDown') return '↓';
  if (keyName.length === 1) return keyName.toUpperCase();
  if (keyName.startsWith('Key')) return keyName.substring(3).toUpperCase();
  if (keyName.startsWith('Digit')) return keyName.substring(5);
  return keyName;
};

/**
 * Simplified GameControls component that displays match buttons and key hints.
 * Handles clicks by passing the current trial index to the response handlers.
 */
const GameControls = ({
  onPositionResponse,
  onAudioResponse,
  positionFeedback,
  audioFeedback,
  gameState,
  missedMatchTrial = null,
  sequence = null,
  currentTrial // *** Added prop to receive the current trial index ***
}) => {
  const { settings } = useSettings();
  const [missedMatchType, setMissedMatchType] = useState(null);

  // Use key bindings from settings with defaults
  const keyBindings = settings?.keyBindings || { visual: 'ArrowLeft', audio: 'ArrowRight' };
  const isDisabled = gameState !== 'playing';

  // Format the keys for display
  const visualKeyDisplay = formatKeyForDisplay(keyBindings.visual);
  const audioKeyDisplay = formatKeyForDisplay(keyBindings.audio);

  // Effect to determine type of missed match (visual or audio)
  useEffect(() => {
    if (missedMatchTrial !== null && sequence && missedMatchTrial >= 0 && missedMatchTrial < sequence.length) {
      const trial = sequence[missedMatchTrial];
      // Check the actual match properties from the sequence if available
      // These properties (positionMatch, audioMatch) would need to be set during sequence generation
      if (trial?.positionMatch) { // Example check, adjust if property names differ
        setMissedMatchType('visual');
      } else if (trial?.audioMatch) { // Example check
        setMissedMatchType('audio');
      } else {
        // Fallback if match properties aren't directly on the sequence item
        // Note: This fallback is less reliable if multiple things happened on the same trial
        setMissedMatchType(null);
      }
    } else {
      setMissedMatchType(null);
    }

    // Clear the missed match type after a short delay (e.g., feedback duration)
    const timer = setTimeout(() => {
      setMissedMatchType(null);
    }, 500); // Adjust timing as needed

    return () => clearTimeout(timer);
  }, [missedMatchTrial, sequence]);

  // --- Handler functions to pass the current trial index ---
  // Use useCallback to prevent unnecessary re-creation of these functions on re-renders
  // unless their dependencies change.
  const handleVisualClick = useCallback(() => {
    // Only trigger if the game is playing and the trial index is valid
    if (!isDisabled && typeof currentTrial === 'number' && currentTrial >= 0) {
      onPositionResponse(currentTrial);
    }
  }, [isDisabled, currentTrial, onPositionResponse]); // Dependencies for the visual click handler

  const handleAudioClick = useCallback(() => {
    // Only trigger if the game is playing and the trial index is valid
    if (!isDisabled && typeof currentTrial === 'number' && currentTrial >= 0) {
       onAudioResponse(currentTrial);
    }
  }, [isDisabled, currentTrial, onAudioResponse]); // Dependencies for the audio click handler
  // --- End handler functions ---

  return (
    <div>
      {/* Button Row - Simplified */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        {/* Position Match Button */}
        <MatchButton
          label="Position Match"
          keyHint={visualKeyDisplay} // keyHint is now only used for title/aria-label in MatchButton
          onClick={handleVisualClick} // *** Use the new handler ***
          // Determine feedback state: prioritize missed match feedback if active
          feedbackState={missedMatchType === 'visual' ? 'missed' : positionFeedback}
          disabled={isDisabled}
        />

        {/* Audio Match Button */}
        <MatchButton
          label="Audio Match"
          keyHint={audioKeyDisplay} // keyHint is now only used for title/aria-label in MatchButton
          onClick={handleAudioClick} // *** Use the new handler ***
          // Determine feedback state: prioritize missed match feedback if active
          feedbackState={missedMatchType === 'audio' ? 'missed' : audioFeedback}
          disabled={isDisabled}
        />
      </div>

      {/* Helper Text - Only visible on non-mobile devices (md and up) */}
      <div className="text-center text-xs text-gray-500 mt-2 hidden md:block">
        Press <kbd className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs border border-gray-300 shadow-sm">{visualKeyDisplay}</kbd> for Position Match,
        <kbd className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs border border-gray-300 shadow-sm ml-1">{audioKeyDisplay}</kbd> for Audio Match,
        <kbd className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs border border-gray-300 shadow-sm ml-1">P</kbd> to Pause
      </div>
    </div>
  );
};

export default GameControls;